import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { BuzerIcon } from "../../BuzerIcons";
import { EffectBurst, Glyph, useModalBackClose, useScrollLock } from "../../components/ui";
import { iconChipStyle } from "../../lib/icons-resolve";
import { REDUCE_MOTION } from "../../lib/theme";
import "./day-ring.css";

// v33h — plávajúce iskričky v pozadí perfektného dňa (pevné pozície, nech neposkakujú pri renderi).
// {x,y}=% v ráme, sz=veľkosť px, d=delay s, dur=trvanie s, dx=bočný drift px.

// deň-kruh — oslavný modal pri splnení návyku: segmentový kruh dňa (1 segment = 1 úloha,
// veľkosť ∝ body; v Lite všetky rovnaké), práve splnený segment sa animovane „naleje".


const polar = (c, r, deg) => {
  const a = ((deg - 90) * Math.PI) / 180;
  return [c + r * Math.cos(a), c + r * Math.sin(a)];
};
const arcPath = (c, r, a0, a1) => {
  const [x0, y0] = polar(c, r, a0);
  const [x1, y1] = polar(c, r, a1);
  return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${a1 - a0 > 180 ? 1 : 0} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
};

function useCountUp(from, to, ms = 600, delay = 250) {
  const [v, setV] = useState(REDUCE_MOTION || from === to ? to : from);
  useEffect(() => {
    if (REDUCE_MOTION || from === to) { setV(to); return; }
    let raf, start;
    const t0 = setTimeout(() => {
      const step = (ts) => {
        if (start == null) start = ts;
        const p = Math.min(1, (ts - start) / ms);
        setV(Math.round(from + (to - from) * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(t0); if (raf) cancelAnimationFrame(raf); };
  }, [from, to, ms, delay]);
  return v;
}

export function DayRing({ tasks, justCompletedId, lite, todayPts, dayMax, doneCount, allDone, perfectStreak = 0, instant = false }) {
  const C = 140, R = 116;  // v33h — kruh o kúsok väčší (rovnaký rám 280×280 = sekcia nezmenená)
  const N = tasks.length;
  // v34 (C1) — výplň sa „naleje" cez stroke-dashoffset: mount s plným offsetom, o 2 framy → 0.
  // Napĺňa sa VŽDY pri mounte (aj oslavný kruh na domovskej dlaždici, aj v modale); jediná výnimka
  // je REDUCE_MOTION (naleje sa hneď, bez pohybu).
  const [filled, setFilled] = useState(REDUCE_MOTION);
  useEffect(() => {
    if (REDUCE_MOTION) return;
    let r2;
    const r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => setFilled(true)); });
    return () => { cancelAnimationFrame(r1); if (r2) cancelAnimationFrame(r2); };
  }, []);
  const just = tasks.find((t) => t.id === justCompletedId);
  const shownPts = useCountUp(todayPts - ((just && just.pts) || 0), todayPts);

  // v34 (C1/ITEM 3) — PERFEKTNÝ DEŇ (oslava): jeden čistý súvislý prstenec, ktorý sa NALEJE
  //   (stroke-dashoffset: obvod → 0) vo farbe témy, so žiarou/aurou vzadu a „Perfektný deň"
  //   v strede. ŽIADNE segmenty a ŽIADNE ikonky úloh v tomto stave. Animuje sa pri mounte
  //   (domovská dlaždica aj perfektný modal). REDUCE_MOTION = naleje sa hneď. Mali-safe:
  //   jedna GPU vrstva max — wrap sa nezväčšuje (žiadny transform), aura+iskričky sú súrodenci.
  if (allDone) {
    const CIRC = 2 * Math.PI * R;
    const capText = (perfectStreak || 0) + 1 >= 2 ? `${(perfectStreak || 0) + 1}. v poradí` : (lite ? `${doneCount}/${N} splnených` : `${todayPts} bodov`);
    return (
      <div className="bz-dayring-wrap">
        {lite && !REDUCE_MOTION && <div className="bz-dayring-glow" />}
        {/* v34 — plávajúce iskričky (confetti) pri perfektnom dni zrušené na želanie */}
        {!lite && <div className={"bz-dayring-aura" + (instant ? " bz-dayring-aura--static" : "")} />}
        <svg viewBox="0 0 280 280" className="bz-dayring-svg" aria-hidden="true">
          {/* jemná stopa pod výplňou (nech je kruh čitateľný aj kým sa napĺňa) */}
          <circle cx={C} cy={C} r={R} fill="none" strokeWidth={26}
            stroke="color-mix(in srgb, var(--brand-primary) 14%, var(--neutral-surface-3))" />
          {/* napĺňajúci sa prstenec vo farbe témy — jeden súvislý kruh, žiadne segmenty */}
          <circle cx={C} cy={C} r={R} fill="none" strokeWidth={26} strokeLinecap="round"
            stroke="var(--brand-primary)" transform={`rotate(-90 ${C} ${C})`}
            className={"bz-dayring-fill" + (lite ? " bz-dayring-fill--lite" : "")}
            strokeDasharray={`${CIRC.toFixed(1)} ${CIRC.toFixed(1)}`}
            strokeDashoffset={filled ? 0 : CIRC.toFixed(1)} />
          {/* v31.9 — jemná čiarkovaná kružnica vnútri (počet čiarok = počet návykov) */}
          {(() => {
            const dr = 88, dcirc = 2 * Math.PI * dr, seg = dcirc / Math.max(1, N);
            const dash = Math.min(6, seg * 0.4);
            return (
              <circle cx={C} cy={C} r={dr} fill="none" stroke="var(--brand-primary)" strokeWidth={1.6}
                strokeDasharray={`${dash} ${seg - dash}`} strokeLinecap="round" opacity={0.5} />
            );
          })()}
        </svg>
        <div className="bz-dayring-center">
          <div className="bz-dayring-perfect" role="text" aria-label="Perfektný deň">
            {instant ? "Perfektný deň" : "Perfektný deň".split("").map((ch, i) => (
              <span key={i} style={{ animationDelay: 400 + i * 45 + "ms" }}>{ch === " " ? " " : ch}</span>
            ))}
          </div>
          <div className="bz-dayring-cap">{capText}</div>
        </div>
      </div>
    );
  }

  // ── IN-PROGRESS (nie perfektný deň) — segmentový kruh dňa + ikonky úloh (nezmenené) ──
  const fillPath = (d, col, L, sw, cap) => (
    <path d={d} fill="none" strokeWidth={sw} strokeLinecap={cap} stroke={col}
      className={"bz-dayring-fill" + (lite ? " bz-dayring-fill--lite" : "")}
      strokeDasharray={`${L.toFixed(1)} ${L.toFixed(1)}`}
      strokeDashoffset={filled ? 0 : L.toFixed(1)} />
  );
  const chipEl = (t, x, y, chip, dim) => {
    const isJust = t.id === justCompletedId;
    return (
      <div key={t.id} title={t.label}
        className={"bz-dayring-chip" + (dim ? " bz-dayring-chip--dim" : "") + (isJust && !lite ? " bz-dayring-chip--pop" : "")}
        style={{ width: chip, height: chip, left: `${(x / 280) * 100}%`, top: `${(y / 280) * 100}%`,
          ...(iconChipStyle(t.icon_color) || {}) }}>
        <Glyph icon={t.icon} size={Math.round(chip * 0.55)} />
      </div>
    );
  };

  let svgBody, chips;
  {
    // návrh B — splnené sa zoskupia od 12-tej do jedného hrubého farebného oblúka
    // (butt spoje + okrúhle „zátky" na koncoch), zvyšok dňa = tenká šedá stopa s ikonkami
    const done = tasks.filter((t) => t.done && t.id !== justCompletedId)
      .concat(just && just.done !== false ? [just] : []);
    const rest = tasks.filter((t) => !t.done);
    const W = (t) => (lite ? 1 : Math.max(t.pts || 0, 1));
    const Wtot = tasks.reduce((a, t) => a + W(t), 0) || 1;
    const GAP = 1.8, SW = 26, RW = 7;
    const K = 360 - Math.max(0, done.length - 1) * GAP - (rest.length ? 8 : GAP);
    let arcs = done.map((t) => Math.max(6, (W(t) / Wtot) * K));
    let restArc = rest.length ? Math.max(10, (rest.reduce((a, t) => a + W(t), 0) / Wtot) * K
      - arcs.reduce((a, x, i) => a + (x - (W(done[i]) / Wtot) * K), 0)) : 0;
    const dab = (deg, col, cls) => (
      <path d={arcPath(C, R, deg, deg + 0.05)} fill="none" strokeWidth={SW} strokeLinecap="round" stroke={col} className={cls} />
    );
    let a = 0;
    const parts = [];
    const chipsArr = [];
    const mids = [];
    done.forEach((t, i) => {
      // progress oblúk (výplň kruhu) je VŽDY vo farbe témy; vlastnú farbu si nesú len ikonky
      const col = "var(--brand-primary)", a1 = a + arcs[i];
      const isJust = t.id === justCompletedId;
      const d = arcPath(C, R, a, a1);
      if (i === 0) parts.push(<g key="dab0">{dab(0, col, isJust ? "bz-dayring-dab bz-dayring-dab--early" : "")}</g>);
      parts.push(
        <g key={t.id}>
          {isJust ? fillPath(d, col, (Math.PI * R * (a1 - a)) / 180, SW, "butt")
            : <path d={d} fill="none" strokeWidth={SW} strokeLinecap="butt" stroke={col} />}
          {i === done.length - 1 && dab(a1 - 0.05, col, isJust ? "bz-dayring-dab" : "")}
        </g>
      );
      mids.push((a + a1) / 2);
      a = a1 + GAP;
    });
    // ikonka pre KAŽDÝ splnený sektor — veľkosť podľa odstupu od susedov, nech sa neprekrývajú
    const doneCap = N <= 8 ? 30 : 22;
    done.forEach((t, i) => {
      let near = Math.min(i > 0 ? mids[i] - mids[i - 1] : 999, i < mids.length - 1 ? mids[i + 1] - mids[i] : 999);
      if (!rest.length && mids.length > 1) near = Math.min(near, 360 - (mids[mids.length - 1] - mids[0]));
      const chord = 2 * 127 * Math.sin((Math.min(near, 60) * Math.PI) / 360);
      const size = Math.max(14, Math.min(doneCap, Math.floor(chord - 3)));
      const [x, y] = polar(C, 127, mids[i]);
      chipsArr.push(chipEl(t, x, y, size, false));
    });
    if (rest.length) {
      const r0 = a + 2.2, r1 = Math.min(r0 + restArc, 356);
      parts.push(<path key="rest" d={arcPath(C, R, r0, r1)} fill="none" strokeWidth={RW} strokeLinecap="round"
        stroke={"color-mix(in srgb, var(--neutral-text-subtle) 18%, var(--neutral-surface-3))"} />);
      // nesplnené úlohy = stlmené ikonky navlečené na šedej stope, nech vidno ČO ešte čaká
      const step = (r1 - r0) / rest.length;
      const chord = 2 * R * Math.sin((step * Math.PI) / 360);
      if (chord >= 17) {
        const size = Math.max(14, Math.min(N <= 8 ? 26 : 20, Math.floor(chord - 4)));
        rest.forEach((t, i) => {
          const [x, y] = polar(C, R, r0 + (i + 0.5) * step);
          chipsArr.push(chipEl(t, x, y, size, true));
        });
      } else {
        // extrém: veľa čakajúcich na malom oblúku — ikonky sa nezmestia, aspoň čiarky
        const restW = rest.reduce((s, t) => s + W(t), 0) || 1;
        let b = r0;
        rest.slice(0, -1).forEach((t, i) => {
          b += (W(t) / restW) * (r1 - r0);
          const [x0, y0] = polar(C, R - 5, b), [x1, y1] = polar(C, R + 5, b);
          parts.push(<line key={"tick" + i} x1={x0} y1={y0} x2={x1} y2={y1} strokeWidth={1.5}
            stroke="var(--neutral-text-subtle)" opacity={0.55} />);
        });
      }
    }
    svgBody = parts;
    chips = chipsArr;
  }
  // IN-PROGRESS render (allDone je vyššie short-circuitnuté): segmentový kruh + ikonky úloh.
  return (
    <div className="bz-dayring-wrap">
      {lite && !REDUCE_MOTION && <div className="bz-dayring-glow" />}
      <svg viewBox="0 0 280 280" className="bz-dayring-svg" aria-hidden="true">
        {svgBody}
      </svg>
      {chips}
      <div className="bz-dayring-center">
        {lite ? (
          <>
            <div className="bz-dayring-num">{doneCount}/{N}</div>
            <div className="bz-dayring-cap">splnených dnes</div>
          </>
        ) : (
          <>
            <div className="bz-dayring-num">{shownPts}</div>
            <div className="bz-dayring-cap">z {dayMax} bodov</div>
            <div className="bz-dayring-count">{doneCount}/{N} splnených</div>
          </>
        )}
      </div>
    </div>
  );
}

// snapshot = { tasks, justCompletedId, todayPts, dayMax, doneCount, allDone, effect } —
// zachytený v toggle() PRED reload(), takže refresh stavu nerozbije bežiacu animáciu.
export function DayRingModal({ snapshot, lite, onClose }) {
  useScrollLock();
  useModalBackClose(onClose);
  const { tasks, justCompletedId, todayPts, dayMax, doneCount, allDone, effect, perfectStreak } = snapshot;
  const just = tasks.find((t) => t.id === justCompletedId);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [snapshot]);  // modal sa nezatvára sám — len ťuknutím / Esc
  // Burst = kúpený „done_effect" (napr. srdiečka) — padá pri KAŽDOM splnení návyku.
  // v27 §5: pri perfektnom dni príde až PO fúzii a shine (nech oslavu korunuje a neprekrýva),
  // pri bežnom splnení hneď po naliatí segmentu.
  const [burstNow, setBurstNow] = useState(false);
  useEffect(() => {
    if (REDUCE_MOTION || lite) { setBurstNow(true); return; }
    const t = setTimeout(() => setBurstNow(true), allDone ? 1800 : 550);
    return () => clearTimeout(t);
  }, [allDone]);
  if (!tasks || !tasks.length) return null;
  return createPortal(
    <div className="bz-dayring-scrim" onClick={onClose} role="dialog" aria-label="Denný pokrok">
      <div className="bz-dayring-card">
        {allDone ? (
          <>
            <div className="bz-dayring-title">
              <BuzerIcon name="star2" size={18} color="var(--brand-primary)" style={{ verticalAlign: "-3px", marginRight: 6 }} />
              Všetko splnené!
            </div>
            <div className="bz-dayring-sub">Dnešný deň je kompletný. Paráda!</div>
          </>
        ) : (
          <>
            <div className="bz-dayring-title">Hotovo!</div>
            <div className="bz-dayring-sub">{(just && just.label) || ""}</div>
          </>
        )}
        <DayRing key={justCompletedId} tasks={tasks} justCompletedId={justCompletedId} lite={lite}
          todayPts={todayPts} dayMax={dayMax} doneCount={doneCount} allDone={allDone} perfectStreak={perfectStreak} />
        <div className="bz-dayring-hint">ťukni pre zatvorenie</div>
      </div>
      {burstNow && (
        <div className="bz-dayring-burst"><EffectBurst effect={lite ? "calm" : effect || "confetti"} /></div>
      )}
    </div>,
    document.body
  );
}
